import React, { useEffect, useState, useCallback } from 'react';
import { Download, Github, Rocket, Loader2, CheckCircle2, AlertCircle, Play, Square, PauseCircle, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../services/api';
import { useWorkspaceStatus } from '../hooks/useWorkspaceStatus';

interface Destination {
  id: 'zip' | 'github' | 'railway';
  label: string;
  ready: boolean;
  reason: string | null;
  endpoint: string;
  method: string;
}

interface DeployPlan {
  workspace_id: string;
  has_code: boolean;
  file_count: number;
  git_initialized: boolean;
  has_github_token: boolean;
  stack_hint: string | null;
  destinations: Destination[];
  next_actions: string[];
}

interface BuildStatus {
  has_job: boolean;
  job_id: string | null;
  status: string | null;
  paused_reason: string | null;
  plan_summary: string | null;
  total_modules: number;
  completed_count: number;
  failed_count: number;
  pending_modules: string[];
  wire_up_status: string | null;
  tests_status: string | null;
  files_created_count: number;
  tool_calls_total: number;
  can_resume: boolean;
}

interface RuntimeStatus {
  running: boolean;
  workspace_id: string;
  port: number | null;
  started_at: string | null;
  expires_at: string | null;
  proxy_base: string | null;
  detected_routes: string[];
  stack: string | null;
  error?: string | null;
}

interface DeployPanelProps {
  workspaceId: string;
}

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  zip: Download,
  github: Github,
  railway: Rocket,
};

export const DeployPanel: React.FC<DeployPanelProps> = ({ workspaceId }) => {
  // ── Shared workspace status (Feb 2026 fix, Emergent Support) ──────
  // Was: DeployPanel and RepoPanel each ran their own polling loop
  // (5s and 3s respectively). That produced UI flicker because each
  // panel saw a different snapshot of the workspace state — buttons
  // could disappear mid-render when the two loops disagreed.
  //
  // Now: both panels subscribe to the SAME `useWorkspaceStatus`
  // hook, which owns a single per-workspace poll and broadcasts the
  // consolidated snapshot to every subscriber. One source of truth,
  // zero flicker, half the backend load.
  const shared = useWorkspaceStatus(workspaceId, 4000);
  const [plan, setPlan] = useState<DeployPlan | null>(null);
  const [build, setBuild] = useState<BuildStatus | null>(null);
  const [runtime, setRuntime] = useState<RuntimeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Form state for the GitHub push modal
  const [showGithubForm, setShowGithubForm] = useState(false);
  const [ghRepo, setGhRepo] = useState('');
  const [ghBranch, setGhBranch] = useState('main');
  const [ghCommit, setGhCommit] = useState('Initial commit from Nexus');
  const [ghCreate, setGhCreate] = useState(true);

  // Mirror the shared snapshot into our local state so the existing
  // render logic (which reads from `plan`/`build`/`runtime`) keeps
  // working unchanged. We NEVER blank out a previous value on error —
  // stability over freshness (matches the RepoPanel invariant).
  useEffect(() => {
    if (shared.deployPlan) setPlan(shared.deployPlan);
    if (shared.buildStatus) setBuild(shared.buildStatus);
    if (shared.runtimeStatus) setRuntime(shared.runtimeStatus);
    if (!shared.loading) setLoading(false);
  }, [shared.deployPlan, shared.buildStatus, shared.runtimeStatus, shared.loading, shared.lastUpdated]);

  const reload = useCallback(async () => {
    await shared.refresh();
  }, [shared]);

  const downloadZip = (dest: Destination) => {
    setBusyId(dest.id);
    const url = `${process.env.REACT_APP_BACKEND_URL}${dest.endpoint}`;
    const token = localStorage.getItem('nexus_token') || '';
    // Use fetch + blob so we can attach the Authorization header.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const dispo = res.headers.get('content-disposition') || '';
        const match = /filename="?([^";]+)/.exec(dispo);
        const fname = match ? match[1] : `${workspaceId}.zip`;
        const dl = document.createElement('a');
        dl.href = URL.createObjectURL(blob);
        dl.download = fname;
        document.body.appendChild(dl);
        dl.click();
        document.body.removeChild(dl);
        toast.success(`Descargado ${fname}`);
      })
      .catch((err) => toast.error(`Descarga falló: ${err.message}`))
      .finally(() => setBusyId(null));
  };

  const pushGithub = async () => {
    if (!ghRepo.includes('/')) {
      toast.error('El formato debe ser owner/repo');
      return;
    }
    setBusyId('github');
    try {
      const result = await apiService.pushWorkspaceToGithub(workspaceId, {
        repo: ghRepo.trim(),
        branch: ghBranch.trim() || 'main',
        commit_message: ghCommit.trim() || 'Initial commit from Nexus',
        create_repo: ghCreate,
        private: true,
      });
      if (result.ok) {
        toast.success(`Push OK · ${result.pushed_files} archivos a ${result.repo}`);
        setShowGithubForm(false);
      } else {
        toast.error(result.error || 'Push falló');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err.message || 'Push falló');
    } finally {
      setBusyId(null);
    }
  };

  const prepareRailway = async () => {
    setBusyId('railway');
    try {
      const result = await apiService.prepareRailway(workspaceId);
      toast.success(`Railway preparado: ${result.files_generated?.length || 0} archivos`);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err.message || 'Preparación falló');
    } finally {
      setBusyId(null);
    }
  };

  const resumeBuild = async () => {
    setBusyId('resume');
    try {
      await apiService.resumeBuild(workspaceId);
      toast.success('Reanudando generación · revisa el Taller');
      await reload();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err.message || 'No se pudo reanudar');
    } finally {
      setBusyId(null);
    }
  };

  const startRuntime = async () => {
    setBusyId('runtime_start');
    try {
      const r = await apiService.runtimeStart(workspaceId);
      if (r.running) toast.success(`App lanzada en puerto ${r.port}`);
      else toast.error(r.error || 'No pude lanzar la app');
      setRuntime(r);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err.message || 'Falló al lanzar');
    } finally {
      setBusyId(null);
    }
  };

  const stopRuntime = async () => {
    setBusyId('runtime_stop');
    try {
      await apiService.runtimeStop(workspaceId);
      toast.success('App detenida');
      setRuntime({ ...(runtime || ({} as any)), running: false });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#0E0F11] border border-white/5 p-8 rounded-xl text-center"
           data-testid="workspace-deploy-panel">
        <Loader2 className="w-8 h-8 text-[#52525B] mx-auto animate-spin" />
      </div>
    );
  }

  if (!plan) return null;

  return (
    <div className="space-y-4" data-testid="workspace-deploy-panel">
      {/* Header */}
      <div className="bg-[#0E0F11] border border-white/5 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-1">
          <Rocket className="w-5 h-5 text-[#A1A1AA]" />
          <h3 className="text-base font-medium text-white">Despliegue del proyecto</h3>
        </div>
        <p className="text-sm text-[#A1A1AA]">
          {plan.has_code ? (
            <>
              Stack detectado:{' '}
              <span className="font-mono text-emerald-300/80">
                {plan.stack_hint || 'desconocido'}
              </span>{' '}· {plan.file_count} archivo(s) en disco
            </>
          ) : (
            'Este workspace todavía no tiene código. Pídele a Nexus que cree o importe un proyecto.'
          )}
        </p>
      </div>

      {/* Build status (resumable) */}
      {build?.has_job && (
        <div
          className={`border rounded-xl p-5 ${
            build.status === 'paused'
              ? 'bg-amber-500/5 border-amber-500/30'
              : build.status === 'running'
              ? 'bg-blue-500/5 border-blue-500/30'
              : 'bg-[#0E0F11] border-white/5'
          }`}
          data-testid="build-status-card"
        >
          <div className="flex items-center gap-2 mb-2">
            {build.status === 'paused' && (
              <PauseCircle className="w-4 h-4 text-amber-400" />
            )}
            {build.status === 'running' && (
              <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
            )}
            {build.status === 'completed' && (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            )}
            <span className="text-sm font-medium text-white">
              {build.status === 'paused'
                ? 'Generación pausada'
                : build.status === 'running'
                ? 'Generación en curso'
                : build.status === 'completed'
                ? 'Generación completa'
                : 'Generación'}
            </span>
          </div>
          <p className="text-xs text-[#A1A1AA] mb-3">
            {build.plan_summary || '(sin resumen del plan)'}
          </p>
          <div className="text-xs font-mono text-[#A1A1AA] mb-3 grid grid-cols-2 gap-1">
            <span>· Módulos: {build.completed_count}/{build.total_modules} completados</span>
            <span>· Archivos: {build.files_created_count}</span>
            <span>
              · Wire-up: {build.wire_up_status || 'pendiente'}
            </span>
            <span>· Tests: {build.tests_status || 'pendiente'}</span>
          </div>
          {build.pending_modules.length > 0 && (
            <p className="text-xs text-amber-300/80 mb-3">
              Pendiente: {build.pending_modules.join(', ')}
            </p>
          )}
          {build.paused_reason && (
            <p className="text-xs text-amber-300 mb-3 font-mono">
              {build.paused_reason}
            </p>
          )}
          {build.can_resume && (
            <button
              onClick={resumeBuild}
              disabled={busyId === 'resume'}
              className="text-xs font-mono uppercase tracking-wider py-2 px-4
                         rounded-lg bg-amber-500/10 text-amber-300 border
                         border-amber-500/30 hover:bg-amber-500/20
                         disabled:opacity-40 transition flex items-center gap-1.5"
              data-testid="build-resume-button"
            >
              <PlayCircle className="w-3.5 h-3.5" />
              {busyId === 'resume' ? 'Reanudando…' : 'Continuar generación'}
            </button>
          )}
        </div>
      )}

      {/* Runtime — probar app generada */}
      {plan.has_code && (
        <div className="bg-[#0E0F11] border border-white/5 rounded-xl p-5"
             data-testid="runtime-card">
          <div className="flex items-center gap-2 mb-3">
            <Play className="w-4 h-4 text-[#A1A1AA]" />
            <span className="text-sm font-medium text-white">
              Probar app generada
            </span>
            {runtime?.running && (
              <span className="ml-auto text-[10px] font-mono uppercase
                               text-emerald-400 bg-emerald-500/10 px-2 py-0.5
                               rounded-full">
                LIVE · puerto {runtime.port}
              </span>
            )}
          </div>
          <p className="text-xs text-[#A1A1AA] mb-3 leading-snug">
            Levanta temporalmente el backend de la app (15 min) y expone un
            proxy HTTP para validarlo antes de exportar o desplegar.
          </p>
          {runtime?.running && runtime.detected_routes.length > 0 && (
            <details className="text-xs font-mono mb-3 text-[#A1A1AA]">
              <summary className="cursor-pointer text-[#71717A]">
                Rutas detectadas ({runtime.detected_routes.length})
              </summary>
              <ul className="mt-2 space-y-0.5 pl-2">
                {runtime.detected_routes.slice(0, 12).map((r, i) => (
                  <li key={i}>· {r}</li>
                ))}
              </ul>
            </details>
          )}
          {runtime?.error && !runtime.running && (
            <pre className="text-[10px] text-red-400 font-mono whitespace-pre-wrap
                            bg-red-500/5 border border-red-500/20 rounded p-2
                            max-h-36 overflow-auto mb-3">
              {runtime.error.slice(0, 600)}
            </pre>
          )}
          <div className="flex gap-2">
            {!runtime?.running ? (
              <button
                onClick={startRuntime}
                disabled={busyId === 'runtime_start'}
                className="text-xs font-mono uppercase tracking-wider py-2 px-3
                           rounded-lg bg-emerald-500/10 text-emerald-300 border
                           border-emerald-500/30 hover:bg-emerald-500/20
                           disabled:opacity-40 transition flex items-center gap-1.5"
                data-testid="runtime-start-button"
              >
                <Play className="w-3.5 h-3.5" />
                {busyId === 'runtime_start' ? 'Lanzando…' : 'Probar app'}
              </button>
            ) : (
              <>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${runtime.proxy_base}/docs?token=${encodeURIComponent(localStorage.getItem('nexus_token') || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-mono uppercase tracking-wider py-2 px-3
                             rounded-lg bg-emerald-500/10 text-emerald-300 border
                             border-emerald-500/30 hover:bg-emerald-500/20
                             transition flex items-center gap-1.5"
                  data-testid="runtime-open-button"
                >
                  Abrir /docs
                </a>
                <button
                  onClick={stopRuntime}
                  disabled={busyId === 'runtime_stop'}
                  className="text-xs font-mono uppercase tracking-wider py-2 px-3
                             rounded-lg bg-white/5 text-white border border-white/10
                             hover:bg-white/10 disabled:opacity-40 transition
                             flex items-center gap-1.5"
                  data-testid="runtime-stop-button"
                >
                  <Square className="w-3.5 h-3.5" />
                  {busyId === 'runtime_stop' ? 'Deteniendo…' : 'Detener'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Destinations */}
      <div className="grid gap-3 md:grid-cols-3">
        {plan.destinations.map((dest) => {
          const Icon = ICONS[dest.id] || Rocket;
          const isBusy = busyId === dest.id;
          return (
            <div
              key={dest.id}
              className={`bg-[#0E0F11] border rounded-xl p-5 transition ${
                dest.ready
                  ? 'border-white/10 hover:border-emerald-500/40'
                  : 'border-white/5 opacity-60'
              }`}
              data-testid={`deploy-card-${dest.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-[#A1A1AA]" />
                <span className="text-sm font-medium text-white">{dest.label}</span>
                {dest.ready ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 ml-auto" />
                ) : (
                  <AlertCircle className="w-3.5 h-3.5 text-amber-400 ml-auto" />
                )}
              </div>
              {dest.reason && (
                <p className="text-xs text-amber-300/80 mb-3 leading-snug">{dest.reason}</p>
              )}
              {dest.id === 'zip' && (
                <button
                  disabled={!dest.ready || isBusy}
                  onClick={() => downloadZip(dest)}
                  className="w-full text-xs font-mono uppercase tracking-wider py-2 px-3
                             rounded-lg bg-emerald-500/10 text-emerald-300 border
                             border-emerald-500/30 hover:bg-emerald-500/20
                             disabled:opacity-40 disabled:cursor-not-allowed transition"
                  data-testid="deploy-action-zip"
                >
                  {isBusy ? 'Descargando…' : 'Descargar zip'}
                </button>
              )}
              {dest.id === 'github' && (
                <button
                  disabled={!dest.ready || isBusy}
                  onClick={() => setShowGithubForm(true)}
                  className="w-full text-xs font-mono uppercase tracking-wider py-2 px-3
                             rounded-lg bg-white/5 text-white border border-white/10
                             hover:bg-white/10 disabled:opacity-40
                             disabled:cursor-not-allowed transition"
                  data-testid="deploy-action-github"
                >
                  {isBusy ? 'Pusheando…' : 'Push a GitHub'}
                </button>
              )}
              {dest.id === 'railway' && (
                <button
                  disabled={!dest.ready || isBusy}
                  onClick={prepareRailway}
                  className="w-full text-xs font-mono uppercase tracking-wider py-2 px-3
                             rounded-lg bg-white/5 text-white border border-white/10
                             hover:bg-white/10 disabled:opacity-40
                             disabled:cursor-not-allowed transition"
                  data-testid="deploy-action-railway"
                >
                  {isBusy ? 'Generando…' : 'Preparar Railway'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Next actions */}
      {plan.next_actions.length > 0 && (
        <div className="bg-[#0A0B0D] border border-white/5 rounded-xl p-4">
          <p className="text-xs font-mono uppercase tracking-wider text-[#52525B] mb-2">
            Próximos pasos
          </p>
          <ul className="space-y-1">
            {plan.next_actions.map((a, i) => (
              <li key={i} className="text-sm text-[#A1A1AA] leading-relaxed">
                · {a}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* GitHub form modal */}
      {showGithubForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center
                        justify-center p-4" onClick={() => setShowGithubForm(false)}>
          <div className="bg-[#0E0F11] border border-white/10 rounded-xl p-6 max-w-md w-full"
               onClick={(e) => e.stopPropagation()}
               data-testid="deploy-github-form">
            <h3 className="text-base font-medium text-white mb-4">Push a GitHub</h3>
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs text-[#A1A1AA]">Repo (owner/name)</span>
                <input
                  type="text"
                  value={ghRepo}
                  onChange={(e) => setGhRepo(e.target.value)}
                  placeholder="miusuario/pocket-signals-3"
                  className="mt-1 w-full bg-[#050505] border border-white/10 rounded-lg
                             px-3 py-2 text-sm text-white"
                  data-testid="deploy-github-repo"
                />
              </label>
              <label className="block">
                <span className="text-xs text-[#A1A1AA]">Branch</span>
                <input
                  type="text"
                  value={ghBranch}
                  onChange={(e) => setGhBranch(e.target.value)}
                  className="mt-1 w-full bg-[#050505] border border-white/10 rounded-lg
                             px-3 py-2 text-sm text-white"
                  data-testid="deploy-github-branch"
                />
              </label>
              <label className="block">
                <span className="text-xs text-[#A1A1AA]">Commit message</span>
                <input
                  type="text"
                  value={ghCommit}
                  onChange={(e) => setGhCommit(e.target.value)}
                  className="mt-1 w-full bg-[#050505] border border-white/10 rounded-lg
                             px-3 py-2 text-sm text-white"
                  data-testid="deploy-github-commit"
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                <input
                  type="checkbox"
                  checked={ghCreate}
                  onChange={(e) => setGhCreate(e.target.checked)}
                  data-testid="deploy-github-create"
                />
                Crear repo si no existe (privado)
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setShowGithubForm(false)}
                className="flex-1 py-2 text-sm text-[#A1A1AA] border border-white/10 rounded-lg
                           hover:bg-white/5 transition"
              >
                Cancelar
              </button>
              <button
                onClick={pushGithub}
                disabled={busyId === 'github' || !ghRepo.includes('/')}
                className="flex-1 py-2 text-sm bg-emerald-500/10 text-emerald-300 border
                           border-emerald-500/30 rounded-lg hover:bg-emerald-500/20
                           disabled:opacity-40 transition"
                data-testid="deploy-github-submit"
              >
                {busyId === 'github' ? 'Pusheando…' : 'Push'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeployPanel;
